import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["size", "weight", "isItalic", "textColor", "children", "token", "truncate"];
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import ScopedCssBaseline from '../StyleBase/ScopedCssBaseline';
import * as VARIATIONS from './constant';
import { convertString } from '../helper';
const Root = styled.p.withConfig({
  displayName: "Text__Root",
  componentId: "sc-asn4rv-0"
})(["", " letter-spacing:-0.14px;color:", ";", " ", " ", " ", " ", ""], ScopedCssBaseline, _ref => {
  let {
    textColor
  } = _ref;
  return "var(".concat(textColor, ")");
}, _ref2 => {
  let {
    as
  } = _ref2;
  return as === 'p' && css(["margin-bottom:var(--size-spacing-04);"]);
}, _ref3 => {
  let {
    token,
    isItalic
  } = _ref3;
  return VARIATIONS.tokens[convertString.constant(token)] ? css(["font:", " var(", ");"], isItalic ? 'italic' : 'normal', "--".concat(token)) : css(["font:var(--text-weight-regular) var(--text-size-sm) var(--text-font-family);&:after{content:' [ERROR] - ", "';font-weight:var(--text-weight-bold);color:var(--danger-text);}"], "".concat(token, " - does not exist"));
}, _ref4 => {
  let {
    token
  } = _ref4;
  return token.includes('xxl') && css(["letter-spacing:-0.72px;"]);
}, _ref5 => {
  let {
    token
  } = _ref5;
  return token.includes('xs') && css(["letter-spacing:0;"]);
}, _ref6 => {
  let {
    $truncate
  } = _ref6;
  return $truncate && css(["white-space:nowrap;overflow:hidden;text-overflow:ellipsis;"]);
});
const Text = /*#__PURE__*/forwardRef(function TextComponent(_ref7, ref) {
  let {
    isItalic,
    textColor,
    children,
    token,
    truncate
  } = _ref7,
      rest = _objectWithoutPropertiesLoose(_ref7, _excluded);

  return /*#__PURE__*/React.createElement(Root, _extends({
    ref: ref,
    isItalic: isItalic,
    textColor: textColor,
    token: token,
    $truncate: truncate
  }, rest), children);
});
Text.tokens = VARIATIONS.tokens;
Text.displayName = 'Text';
Text.Style = Root;
Text.defaultProps = {
  isItalic: false,
  textColor: '--neutral-text-high-emphasis',
  token: Text.tokens.TEXT_MD_REGULAR,
  as: 'p',
  forwardedAs: undefined,
  truncate: false
};
process.env.NODE_ENV !== "production" ? Text.propTypes = {
  /**
   * Sets the italic font style.
   */
  isItalic: PropTypes.bool,

  /**
   * Set element type to be rendered for this component. By default, it will render a `<p>` element.
   */
  as: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),

  /**
   * Pass as properties to this component if it is encapsulated by the `styled` of the Styled Component.
   */
  forwardedAs: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),

  /**
   * The text to be rendered.
   */
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

  /**
   * Sets the text color based on design tokens.
   */
  textColor: PropTypes.string,

  /**
   * Sets font size.
   */
  token: PropTypes.oneOf([Text.tokens.TEXT_XXL_BOLD, Text.tokens.TEXT_XXL_EXTRABOLD, Text.tokens.TEXT_XXL_REGULAR, Text.tokens.TEXT_XL_BOLD, Text.tokens.TEXT_XL_EXTRABOLD, Text.tokens.TEXT_XL_REGULAR, Text.tokens.TEXT_LG_BOLD, Text.tokens.TEXT_LG_EXTRABOLD, Text.tokens.TEXT_LG_REGULAR, Text.tokens.TEXT_MD_BOLD, Text.tokens.TEXT_MD_EXTRABOLD, Text.tokens.TEXT_MD_REGULAR, Text.tokens.TEXT_SM_BOLD, Text.tokens.TEXT_SM_EXTRABOLD, Text.tokens.TEXT_SM_REGULAR, Text.tokens.TEXT_XS_BOLD, Text.tokens.TEXT_XS_EXTRABOLD, Text.tokens.TEXT_XS_REGULAR]),

  /**
   * Identifies that the text will be truncated when it exceeds the available size.
   */
  truncate: PropTypes.bool
} : void 0;
export default Text;